import { c as _c } from "react/compiler-runtime";
import { Box } from "@mui/material";
import React, { useMemo } from "react";
import { ArgumentType } from "#/entities/argument/model/argumentObject";
import { useBinaryTreePositioning } from "#/entities/dataStructures/binaryTree/hooks/useBinaryTreePositioning";
import { BinaryNode } from "#/entities/dataStructures/binaryTree/ui/BinaryNode";
import { GraphEdge } from "#/entities/dataStructures/graph/ui/GraphEdge";
import { GraphNode } from "#/entities/dataStructures/graph/ui/GraphNode";
import { LinkedListNode } from "#/entities/dataStructures/linkedList/ui/LinkedListNode";
import { selectMinXOffset, type TreeData } from "#/entities/dataStructures/node/model/nodeSlice";
import { useAppSelector } from "#/store/hooks";
const nodeComponentMap = {
  [ArgumentType.LINKED_LIST]: LinkedListNode,
  [ArgumentType.BINARY_TREE]: BinaryNode,
  [ArgumentType.GRAPH]: GraphNode
};
type NodesViewProps = {
  treeName: string;
  data: TreeData;
  style?: React.CSSProperties;
  sx?: React.CSSProperties;
  horizontalAlign?: "start" | "center";
};
const NODE_WIDTH = 42;
export const NodesView: React.FC<NodesViewProps> = t0 => {
  const $ = _c(31);
  const {
    treeName,
    data,
    style,
    sx,
    horizontalAlign: t1
  } = t0;
  const horizontalAlign = t1 === undefined ? "start" : t1;
  const adjustXOffset = data.type === ArgumentType.LINKED_LIST;
  const offset = useAppSelector(selectMinXOffset(treeName, adjustXOffset)) ?? 0;
  useBinaryTreePositioning(treeName, data);
  const Node = nodeComponentMap[data.type];
  style?.left;
  let t2;
  if ($[0] !== data.nodes.entities || $[1] !== data.rootId || $[2] !== data.type || $[3] !== horizontalAlign || $[4] !== offset || $[5] !== style?.left) {
    bb0: {
      const baseLeft = (Number(style?.left) || 0) - offset;
      if (horizontalAlign !== "center" || data.type !== ArgumentType.BINARY_TREE) {
        t2 = baseLeft;
        break bb0;
      }
      const nodes = Object.values(data.nodes.entities).filter(_temp);
      if (nodes.length === 0) {
        t2 = baseLeft;
        break bb0;
      }
      const rootNode = data.rootId ? data.nodes.entities[data.rootId] : undefined;
      const anchorCenterX = rootNode ? rootNode.x + NODE_WIDTH / 2 : (() => {
        const minX = Math.min(...nodes.map(_temp2));
        const maxX = Math.max(...nodes.map(_temp3));
        return (minX + maxX + NODE_WIDTH) / 2;
      })();
      if (baseLeft === 0) {
        t2 = `calc(50% - ${anchorCenterX}px)`;
        break bb0;
      }
      const leftSign = baseLeft >= 0 ? "+" : "-";
      let t3;
      if ($[7] !== baseLeft) {
        t3 = Math.abs(baseLeft);
        $[7] = baseLeft;
        $[8] = t3;
      } else {
        t3 = $[8];
      }
      t2 = `calc(50% ${leftSign} ${t3}px - ${anchorCenterX}px)`;
    }
    $[0] = data.nodes.entities;
    $[1] = data.rootId;
    $[2] = data.type;
    $[3] = horizontalAlign;
    $[4] = offset;
    $[5] = style?.left;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const left = t2;
  let t3;
  if ($[9] !== sx) {
    t3 = {
      position: "absolute",
      width: "100%",
      height: "100%",
      ...sx
    };
    $[9] = sx;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== left || $[12] !== style) {
    t4 = {
      ...style,
      left,
      transition: "left 0.05s ease-in-out"
    };
    $[11] = left;
    $[12] = style;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== data.edges.entities) {
    t5 = Object.values(data.edges.entities);
    $[14] = data.edges.entities;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t5 || $[17] !== treeName) {
    t6 = t5.map(edge => edge && <GraphEdge key={edge.id} treeName={treeName} {...edge} />);
    $[16] = t5;
    $[17] = treeName;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== data.nodes.entities) {
    t7 = Object.values(data.nodes.entities);
    $[19] = data.nodes.entities;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== Node || $[22] !== data.type || $[23] !== t7 || $[24] !== treeName) {
    t8 = t7.map(node_2 => node_2 && <Node key={node_2.id} treeName={treeName} type={data.type} {...node_2} />);
    $[21] = Node;
    $[22] = data.type;
    $[23] = t7;
    $[24] = treeName;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t3 || $[27] !== t4 || $[28] !== t6 || $[29] !== t8) {
    t9 = <Box sx={t3} style={t4}>{t6}{t8}</Box>;
    $[26] = t3;
    $[27] = t4;
    $[28] = t6;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
};
function _temp(node) {
  return node !== undefined;
}
function _temp2(node_0) {
  return node_0.x;
}
function _temp3(node_1) {
  return node_1.x;
}